import { normalizeFilterValue } from 'charts/common/filterValues';
import { ISSUE_CHART_FILTER_DESCRIPTORS } from 'charts/common/issueChartFilters/issueChartFilterDescriptors';
import { IssueChartFilterKind } from 'shared/domain/analytics/issueChartFilters';
import {
  AnalyticsTranslationKeys,
  CommonTranslationKeys,
  InspectionTranslationKeys,
} from 'intl/keys';
import type { TranslationKey } from 'intl/types/translationKey';
import type { ChartFilter, ChartFiltersUnion } from 'shared/types/analytics';
import { isNonEmptyArray } from 'shared/utils/array';
import { deepEqual } from 'shared/utils/deepEqual';
import type { ChartContext } from 'views/analytics/types';

/** Filter dimensions every chart of a dashboard section shares. */
export enum SectionSharedFilter {
  dateRange = 'dateRange',
  process = 'process',
  sites = 'sites',
  author = 'author',
  assignee = 'assignee',
  originators = 'originators',
  workType = 'workType',
  impact = 'impact',
}

export function isDateRangeSharedFilter(shared: SectionSharedFilter): boolean {
  return shared === SectionSharedFilter.dateRange;
}

/** Charts identify their filters by `labelId`; shared dimensions are matched on it. */
const SHARED_FILTER_LABEL_IDS: Record<SectionSharedFilter, TranslationKey> = {
  [SectionSharedFilter.dateRange]: AnalyticsTranslationKeys.dateRange,
  [SectionSharedFilter.process]: CommonTranslationKeys.process,
  [SectionSharedFilter.sites]: AnalyticsTranslationKeys.sites,
  [SectionSharedFilter.author]: InspectionTranslationKeys.tableAuthor,
  [SectionSharedFilter.assignee]: ISSUE_CHART_FILTER_DESCRIPTORS[IssueChartFilterKind.assignee].labelId,
  [SectionSharedFilter.originators]: ISSUE_CHART_FILTER_DESCRIPTORS[IssueChartFilterKind.originators].labelId,
  [SectionSharedFilter.workType]: ISSUE_CHART_FILTER_DESCRIPTORS[IssueChartFilterKind.workType].labelId,
  [SectionSharedFilter.impact]: ISSUE_CHART_FILTER_DESCRIPTORS[IssueChartFilterKind.impact].labelId,
};

/** The dashboard sections with filters of their own; their values are kept per section. */
export enum AnalyticsSection {
  issues = 'issues',
  inspections = 'inspections',
}

/** The filters a dashboard section shows above its charts. */
export type SectionFiltersDefinition = Readonly<{
  section: AnalyticsSection;
  filters: readonly SectionSharedFilter[];
}>;

export const ISSUES_SECTION_FILTERS: SectionFiltersDefinition = {
  section: AnalyticsSection.issues,
  filters: [
    SectionSharedFilter.dateRange,
    SectionSharedFilter.process,
    SectionSharedFilter.sites,
    SectionSharedFilter.assignee,
    SectionSharedFilter.originators,
    SectionSharedFilter.workType,
    SectionSharedFilter.impact,
  ],
};

export const INSPECTIONS_SECTION_FILTERS: SectionFiltersDefinition = {
  section: AnalyticsSection.inspections,
  filters: [
    SectionSharedFilter.dateRange,
    SectionSharedFilter.process,
    SectionSharedFilter.sites,
    SectionSharedFilter.author,
  ],
};

export type SectionChartRuntime = ChartContext<ChartFiltersUnion>;

export type SharedFilterState = Readonly<{
  shared: SectionSharedFilter;
  /** The first chart's filter for this dimension, offering the options of every chart. */
  filter: ChartFilter<unknown>;
  /** The section's value: the one chosen here, or the one most charts hold before anything was chosen. */
  value: unknown;
  /** Some charts hold their own value for this dimension (set in their details). */
  isMixed: boolean;
}>;

export type SharedFilterValues = ReadonlyMap<SectionSharedFilter, unknown>;

function getChartFilters(runtime: SectionChartRuntime): ChartFilter<unknown>[] {
  const { filters } = runtime;
  if (!filters) {
    return [];
  }
  return Object.values(filters).flatMap(
    (group: { filters: ChartFilter<unknown>[] } | undefined) =>
      group?.filters ?? []
  );
}

export function areSharedFilterValuesEqual(a: unknown, b: unknown): boolean {
  return deepEqual(normalizeFilterValue(a), normalizeFilterValue(b));
}

/** The chart's filter for a shared dimension, or undefined when the chart lacks it. */
export function findSharedFilter(
  runtime: SectionChartRuntime,
  shared: SectionSharedFilter
): ChartFilter<unknown> | undefined {
  const labelId = SHARED_FILTER_LABEL_IDS[shared];
  return getChartFilters(runtime).find((filter) => filter.labelId === labelId);
}

/** The value most of the filters hold; `preferred` wins a tie, then the first chart's value. */
function mostCommonValue(filters: readonly ChartFilter<unknown>[], preferred: unknown): unknown {
  const countHolding = (value: unknown): number =>
    filters.filter((filter) => areSharedFilterValuesEqual(filter.value, value)).length;
  const counted = [preferred, ...filters.map(({ value }) => value)].map((value) => ({
    value,
    count: countHolding(value),
  }));
  return counted.reduce((most, next) => (next.count > most.count ? next : most)).value;
}

/** One filter offering the options and labels of every chart (they may hold different ones). */
function mergeFilterOptions(first: ChartFilter<unknown>, filters: readonly ChartFilter<unknown>[]): ChartFilter<unknown> {
  const available = filters.flatMap((filter) => filter.available ?? []);
  return {
    ...first,
    available: first.available && Array.from(new Map(available.map((option) => [option._id, option])).values()),
    labels: first.labels && Object.assign({}, ...filters.map((filter) => filter.labels)),
  };
}

export type SharedFilterValueSources = Readonly<{
  /** The value chosen in the section before, if any. */
  chosen?: unknown;
  /** The value a cleared filter gets: preferred when charts are split evenly. */
  cleared: unknown;
}>;

/**
 * The section's state of one dimension: the value chosen in the section while
 * some chart still holds it, otherwise the value most charts hold. Charts
 * holding another value have their own filter, which overrides the section's.
 */
export function resolveSharedFilterState(
  runtimes: readonly SectionChartRuntime[],
  shared: SectionSharedFilter,
  { chosen, cleared }: SharedFilterValueSources
): SharedFilterState | undefined {
  const filters = runtimes
    .map((runtime) => findSharedFilter(runtime, shared))
    .filter((filter): filter is ChartFilter<unknown> => filter !== undefined);

  const [first] = filters;
  if (!first) {
    return undefined;
  }

  const isChosenHeld = chosen !== undefined && filters.some((filter) => areSharedFilterValuesEqual(filter.value, chosen));
  const value = isChosenHeld ? chosen : mostCommonValue(filters, cleared);
  return {
    shared,
    filter: mergeFilterOptions(first, filters),
    value,
    isMixed: filters.some((filter) => !areSharedFilterValuesEqual(filter.value, value)),
  };
}

/** A filter of the chart holding its own value instead of the section's. */
type CustomSharedFilter = Readonly<{ filter: ChartFilter<unknown>; sectionValue: unknown }>;

/** The shared dimensions of the chart whose values differ from the section values. */
function findCustomSharedFilters(
  runtime: SectionChartRuntime,
  sectionValues: SharedFilterValues
): CustomSharedFilter[] {
  return Array.from(sectionValues).flatMap(([shared, sectionValue]) => {
    const filter = findSharedFilter(runtime, shared);
    return filter && !areSharedFilterValuesEqual(filter.value, sectionValue) ? [{ filter, sectionValue }] : [];
  });
}

/** True when any shared dimension of the chart differs from the section values. */
export function hasCustomSharedFilters(
  runtime: SectionChartRuntime,
  sectionValues: SharedFilterValues
): boolean {
  return isNonEmptyArray(findCustomSharedFilters(runtime, sectionValues));
}

/**
 * Gives the chart the section values back in every shared dimension where it
 * holds its own: it follows the section again. Its filters of other
 * dimensions stay as set in its details.
 */
export function restoreSectionValues(runtime: SectionChartRuntime, sectionValues: SharedFilterValues): void {
  findCustomSharedFilters(runtime, sectionValues).forEach(({ filter, sectionValue }) => filter.setter(sectionValue));
}

/**
 * Sets a new section value on the charts that follow the section (they hold
 * its previous value), through each chart's own setter, which persists the
 * chart filters and reloads the chart. Charts with their own value for the
 * dimension keep it: a chart's filter overrides the section's.
 */
export function applySectionFilterValue(
  runtimes: readonly SectionChartRuntime[],
  shared: SectionSharedFilter,
  { previous, next }: Readonly<{ previous: unknown; next: unknown }>
): void {
  runtimes.forEach((runtime) => {
    const filter = findSharedFilter(runtime, shared);
    if (
      filter &&
      areSharedFilterValuesEqual(filter.value, previous) &&
      !areSharedFilterValuesEqual(filter.value, next)
    ) {
      filter.setter(next);
    }
  });
}
